<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useScrollSpy } from '@/composables/useScrollSpy'
import BrandLogo from '@/components/brand/BrandLogo.vue'
import LanguageSelector from '@/components/layout/LanguageSelector.vue'
import MobileMenu from '@/components/layout/MobileMenu.vue'

const { t } = useI18n()
const mobileMenuOpen = ref(false)

const sectionIds = [
  'start-here',
  'warning-signs',
  'verification',
  'countering-disinfo',
  'manipulation',
  'safety',
  'rapid-response',
  'resources',
]

const { activeSection } = useScrollSpy(sectionIds)

const navItems = [
  { id: 'warning-signs', key: 'nav.warning_signs' },
  { id: 'verification', key: 'nav.verify' },
  { id: 'safety', key: 'nav.stay_safe' },
  { id: 'rapid-response', key: 'nav.respond' },
  { id: 'resources', key: 'nav.resources' },
]

function scrollToSection(id: string) {
  const el = document.getElementById(id)
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
  mobileMenuOpen.value = false
}
</script>

<template>
  <header
    class="sticky top-0 z-40 glass dark:glass-dark border-b border-white/20 dark:border-slate-700/30 no-print"
  >
    <div class="section-container">
      <div class="flex items-center justify-between h-16">
        <router-link
          :to="`/${$i18n.locale}`"
          class="flex items-center gap-2 shrink-0"
          :aria-label="'Signal Before Noise — ' + t('nav.guide')"
        >
          <BrandLogo variant="full" theme="light" />
        </router-link>

        <nav
          class="hidden lg:flex items-center gap-1 overflow-x-auto"
          :aria-label="t('nav.guide')"
        >
          <button
            v-for="item in navItems"
            :key="item.id"
            type="button"
            class="px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
            :class="[
              activeSection === item.id
                ? 'text-primary bg-primary-soft dark:bg-primary/20'
                : 'text-muted hover:text-text dark:text-dark-muted dark:hover:text-dark-text hover:bg-slate-100 dark:hover:bg-slate-800',
            ]"
            @click="scrollToSection(item.id)"
          >
            {{ t(item.key) }}
          </button>
        </nav>

        <div class="flex items-center gap-2">
          <LanguageSelector class="hidden sm:block" />

          <a
            href="#start-here"
            class="hidden md:inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-primary rounded-xl hover:bg-blue-700 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            @click.prevent="scrollToSection('start-here')"
          >
            {{ t('nav.read_guide') }}
          </a>

          <button
            type="button"
            class="lg:hidden p-2 rounded-lg text-muted hover:text-text hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            :aria-label="t('nav.open_menu')"
            @click="mobileMenuOpen = true"
          >
            <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <MobileMenu
      :open="mobileMenuOpen"
      :nav-items="navItems"
      :active-section="activeSection"
      @close="mobileMenuOpen = false"
      @navigate="scrollToSection"
    />
  </header>
</template>
